import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Grid,
  Heading,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  SimpleGrid,
  Text,
  Flex,
  Icon,
  Card,
  CardHeader,
  CardBody,
  Stack,
  Progress,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Button,
  Spinner,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import { 
  FiUsers, 
  FiCheckCircle, 
  FiClock, 
  FiStar,
  FiEye,
  FiChevronRight
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import unifiedService from '../../services/api/unifiedService';
import { getNominationStatusColor, getNominationStatusLabel } from '../../utils/evaluationStatusUtils';

/**
 * Dashboard component for committee members
 * Displays statistics and recent candidates for review
 */
const Dashboard = () => {
  const navigate = useNavigate();
  
  // State
  const [stats, setStats] = useState({
    totalAssigned: 0,
    evaluated: 0,
    pending: 0,
    averageScore: 0,
  });
  const [recentCandidates, setRecentCandidates] = useState([]);
  const [pendingEvaluations, setPendingEvaluations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch dashboard data
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        // Fetch dashboard data using unified service
        const committeeData = await unifiedService.getDashboardData();
        
        // Combine data for committee dashboard
        const dashboardStats = {
          totalAssigned: committeeData.stats?.totalAssigned || 0,
          evaluated: committeeData.stats?.evaluated || 0,
          pending: committeeData.stats?.pending || 0,
          averageScore: committeeData.stats?.averageScore || 0,
        };
        
        // Update state with response data
        setStats(dashboardStats);
        setRecentCandidates(committeeData?.recentCandidates || []);
        setPendingEvaluations(committeeData?.pendingEvaluations || []);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        setError('فشل في تحميل البيانات. يرجى المحاولة مرة أخرى.');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchDashboardData();
  }, []);
  
  // Show loading spinner
  if (isLoading) {
    return (
      <Container maxW="container.xl" py={8}>
        <Flex justify="center" align="center" minH="400px">
          <Spinner size="xl" color="blue.500" />
        </Flex>
      </Container>
    );
  }

  // Show error message
  if (error) {
    return (
      <Container maxW="container.xl" py={8}>
        <Alert status="error">
          <AlertIcon />
          {error}
        </Alert>
      </Container>
    );
  }

  // Calculate completion percentage
  const completionPercentage = stats.totalAssigned > 0 
    ? Math.round((stats.evaluated / stats.totalAssigned) * 100) 
    : 0;
  
  // View candidate details
  const viewCandidate = (id) => {
    navigate(`/committee/candidates/${id}`);
  };
  
  // Centralized helpers
  const getStatusColor = (status) => getNominationStatusColor(status);
  const getStatusText = (status) => getNominationStatusLabel(status);
  
  return (
    <Container maxW="container.xl" py={8}>
      <Heading as="h1" mb={8}>لوحة التحكم</Heading>
      
      {/* Stats */}
      <SimpleGrid columns={{ base: 1, md: 4 }} spacing={6} mb={8}>
        <Card>
          <CardBody>
            <Stat>
              <Flex align="center">
                <Box
                  p={2}
                  borderRadius="md"
                  bg="blue.50"
                  color="blue.500"
                  me={4}
                >
                  <Icon as={FiUsers} boxSize={6} />
                </Box>
                <Box>
                  <StatLabel>إجمالي المرشحين</StatLabel>
                  <StatNumber>{stats.totalAssigned}</StatNumber>
                  <StatHelpText>المعينين لك</StatHelpText>
                </Box>
              </Flex>
            </Stat>
          </CardBody>
        </Card>
        
        <Card>
          <CardBody>
            <Stat>
              <Flex align="center">
                <Box
                  p={2}
                  borderRadius="md"
                  bg="green.50"
                  color="green.500"
                  me={4}
                >
                  <Icon as={FiCheckCircle} boxSize={6} />
                </Box>
                <Box>
                  <StatLabel>تم تقييمهم</StatLabel>
                  <StatNumber>{stats.evaluated}</StatNumber>
                  <StatHelpText>من أصل {stats.totalAssigned}</StatHelpText>
                </Box>
              </Flex>
            </Stat>
          </CardBody>
        </Card>
        
        <Card>
          <CardBody>
            <Stat>
              <Flex align="center">
                <Box
                  p={2}
                  borderRadius="md"
                  bg="yellow.50"
                  color="yellow.500"
                  me={4}
                >
                  <Icon as={FiClock} boxSize={6} />
                </Box>
                <Box>
                  <StatLabel>في انتظار التقييم</StatLabel>
                  <StatNumber>{stats.pending}</StatNumber>
                  <StatHelpText>بحاجة للمراجعة</StatHelpText>
                </Box>
              </Flex>
            </Stat>
          </CardBody>
        </Card>
        
        <Card>
          <CardBody>
            <Stat>
              <Flex align="center">
                <Box
                  p={2}
                  borderRadius="md"
                  bg="purple.50"
                  color="purple.500"
                  me={4}
                >
                  <Icon as={FiStar} boxSize={6} />
                </Box>
                <Box>
                  <StatLabel>متوسط التقييم</StatLabel>
                  <StatNumber>{stats.averageScore.toFixed(1)}/10</StatNumber>
                  <StatHelpText>لجميع المرشحين</StatHelpText>
                </Box>
              </Flex>
            </Stat>
          </CardBody>
        </Card>
      </SimpleGrid>
      
      {/* Progress */}
      <Card mb={8}>
        <CardHeader>
          <Heading size="md">تقدم التقييم</Heading>
        </CardHeader>
        <CardBody>
          <Stack spacing={4}>
            <Flex justify="space-between">
              <Text>اكتمال التقييم</Text>
              <Text fontWeight="bold">{completionPercentage}%</Text>
            </Flex>
            <Progress 
              value={completionPercentage} 
              size="lg" 
              colorScheme="blue" 
              borderRadius="md"
            />
            <Text fontSize="sm" color="gray.500">
              تم تقييم {stats.evaluated} من أصل {stats.totalAssigned} مرشح
            </Text>
          </Stack>
        </CardBody>
      </Card>
      
      <Grid templateColumns={{ base: '1fr', lg: 'repeat(2, 1fr)' }} gap={8}>
        {/* Pending Evaluations */}
        <Card>
          <CardHeader>
            <Flex justify="space-between" align="center">
              <Heading size="md">في انتظار التقييم</Heading>
              <Button 
                rightIcon={<FiChevronRight />} 
                variant="ghost" 
                colorScheme="blue" 
                size="sm"
                onClick={() => navigate('/committee/candidates')}
              >
                عرض الكل
              </Button>
            </Flex>
          </CardHeader>
          <CardBody>
            <Box overflowX="auto">
              <Table variant="simple">
                <Thead>
                  <Tr>
                    <Th>المرشح</Th>
                    <Th>المنصب</Th>
                    <Th>تاريخ التعيين</Th>
                    <Th>الإجراءات</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {isLoading ? (
                    <Tr>
                      <Td colSpan={4} textAlign="center" py={4}>
                        جاري تحميل البيانات...
                      </Td>
                    </Tr>
                  ) : pendingEvaluations.length === 0 ? (
                    <Tr>
                      <Td colSpan={4} textAlign="center" py={4}>
                        لا توجد تقييمات معلقة
                      </Td>
                    </Tr>
                  ) : (
                    pendingEvaluations.slice(0, 5).map((candidate) => (
                      <Tr key={candidate.id}>
                        <Td fontWeight="medium">{candidate.name}</Td>
                        <Td>{candidate.position}</Td>
                        <Td>{new Date(candidate.assignedDate).toLocaleDateString('ar-SA')}</Td>
                        <Td>
                          <Button
                            leftIcon={<FiEye />}
                            colorScheme="blue"
                            size="sm"
                            onClick={() => viewCandidate(candidate.id)}
                          >
                            تقييم
                          </Button>
                        </Td>
                      </Tr>
                    ))
                  )}
                </Tbody>
              </Table>
            </Box>
          </CardBody>
        </Card>
        
        {/* Recent Candidates */}
        <Card>
          <CardHeader>
            <Heading size="md">المرشحون المضافون مؤخراً</Heading>
          </CardHeader>
          <CardBody>
            <Box overflowX="auto">
              <Table variant="simple">
                <Thead>
                  <Tr>
                    <Th>المرشح</Th>
                    <Th>الفرع</Th>
                    <Th>المركز</Th>
                    <Th>المنصب</Th>
                    <Th>تاريخ التقديم</Th>
                    <Th>حالة التقييم</Th>
                    <Th>الإجراءات</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {isLoading ? (
                    <Tr>
                      <Td colSpan={7} textAlign="center" py={4}>
                        جاري تحميل البيانات...
                      </Td>
                    </Tr>
                  ) : recentCandidates.length === 0 ? (
                    <Tr>
                      <Td colSpan={7} textAlign="center" py={4}>
                        لا يوجد مرشحون مضافون مؤخراً
                      </Td>
                    </Tr>
                  ) : (
                    recentCandidates.map((candidate) => (
                      <Tr key={candidate.id}>
                        <Td fontWeight="medium">{candidate.name}</Td>
                        <Td>{candidate.branch}</Td>
                        <Td>{candidate.center}</Td>
                        <Td>{candidate.position}</Td>
                        <Td>{new Date(candidate.applicationDate).toLocaleDateString('ar-SA')}</Td>
                        <Td>
                          <Badge colorScheme={getStatusColor(candidate.status)}>
                            {getStatusText(candidate.status)}
                          </Badge>
                        </Td>
                        <Td>
                          <Button
                            leftIcon={<FiEye />}
                            colorScheme="blue"
                            size="sm"
                            variant="ghost"
                            onClick={() => viewCandidate(candidate.id)}
                          >
                            عرض
                          </Button>
                        </Td>
                      </Tr>
                    ))
                  )}
                </Tbody>
              </Table>
            </Box>
          </CardBody>
        </Card>
      </Grid>
    </Container>
  );
};

export default Dashboard;
